import {useEffect} from "react";
import {useQuery} from "@tanstack/react-query";
import {toast} from "sonner";

import {TOAST_CONFIG} from "@/lib/constansts";
import {getPagination} from "@/src/modules/core/lib";
import {ITEMS_PER_PAGE} from "@/src/modules/core/utils/constants";

import {directMessagesService} from "../services";

export const DIRECT_MESSAGE_THREADS_QUERY_KEY = "directmessage-threads";

export type DirectMessageListOptions = {
  mergeSent: boolean;
  unread: boolean;
};

type UseDirectMessageThreadsParams = {
  mergeSent?: boolean;
  page?: number;
  rows?: number;
  unread?: boolean;
  enabled?: boolean;
};

export default function useDirectMessageThreads({
  mergeSent = false,
  page = 1,
  rows = ITEMS_PER_PAGE,
  unread = false,
  enabled = true,
}: UseDirectMessageThreadsParams = {}) {
  const listOptions: DirectMessageListOptions = {mergeSent, unread};

  const {data, isSuccess, isPending, isError, error, isFetching} = useQuery({
    queryKey: [DIRECT_MESSAGE_THREADS_QUERY_KEY, listOptions, page, rows],
    queryFn: () =>
      directMessagesService.getConversationThreads({
        mergeSent,
        unread,
        page,
        limit: rows,
      }),
    enabled,
    retry: false,
    networkMode: "offlineFirst",
  });

  useEffect(() => {
    if (isError && error) {
      toast.error((error as {code?: string})?.code ?? "Error", TOAST_CONFIG);
    }
  }, [isError, error]);

  const threads = isSuccess && data ? data.data : [];
  const totalRecords = data?.meta?.total ?? 0;
  const {start, end} = getPagination(totalRecords, rows, page);

  return {
    threads,
    totalRecords,
    start,
    end,
    isPending,
    isFetching,
  };
}
